import { Button, Header, Icon, LoadingSpinner } from '@/components/Generic'
import { observer } from 'mobx-react'
import type { CSSProperties } from 'react'
import { useContext, useEffect, useState } from 'react'
import { StoreContext } from '@store/Root.store'
import { LanguageSelect } from '@/components/LanguageSelect/LanguageSelect'
import '@/components/LanguageSelect/LanguageSelect.css'
import { forceRedirect } from '@/methods/forceRedirect'
import { axiosInstance } from '@/methods/axiosConfig'
import { devLog } from '@/methods/devLog'
import { Language } from '@/types/translations'
import { API_URL, JOINTSIGNING_SERVICE_PATH, ONBOARDING_SERVICE_PATH } from '@/constants/apiUrl'
import { isLocalDev } from '@/constants/envs'
import { Illustration } from '@/components/Illustration/Illustration'
import { cx } from '@/utils/cx'

const LANGUAGES_LIST = ['en', 'de']

interface WelcomeData {
  isTokenExpired: boolean
  isChangeLanguagePossible: boolean
  firstName: string
  lastName: string
  proceedUrl: string
}

export const WelcomeEnpalPage = observer(() => {
  const urlParams = new URLSearchParams(window.location.search)
  const proceedToken = urlParams.get('proceedToken')
  const proceedSource = urlParams.get('proceedSource')
  const isJointSigning = !!urlParams.get('isJointSigning')

  const store = useContext(StoreContext)
  const { language } = store.TranslationsState
  const { isMobile } = store.AppState
  const { width, height } = store.AppState.screenSize
  const { WelcomeEnpalPage: trans } = store.TranslationsState.translations

  const [switchingLanguage, setSwitchingLanguage] = useState(false)
  const [isChangeLanguagePossible, setIsChangeLanguagePossible] = useState(false)
  const [btnLoading, setBtnLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [welcomeData, setWelcomeData] = useState<WelcomeData>()

  // Mobile utilities
  const [mobileContainerHeight, setMobileContainerHeight] = useState('')

  const getUserData = async () => {
    setLoading(true)
    let pathUrl = `${ONBOARDING_SERVICE_PATH}/get-welcome-data/${proceedToken}`

    if (isJointSigning) {
      pathUrl = `${JOINTSIGNING_SERVICE_PATH}/get-welcome-data/${proceedToken}?proceedSource=${proceedSource}`
    }

    try {
      const { data } = await axiosInstance.get<WelcomeData>(`${API_URL}/${pathUrl}`, {
        withCredentials: false,
      })
      setWelcomeData(data)
      setIsChangeLanguagePossible(data?.isChangeLanguagePossible)
    } catch (error) {
      devLog('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  const handleProceed = () => {
    if (!btnLoading) {
      setBtnLoading(true)
      welcomeData?.proceedUrl && forceRedirect(welcomeData?.proceedUrl)
    }
  }

  // not implemented in joint signing
  const handleChangeLanguage = async (language: string) => {
    setSwitchingLanguage(true)

    try {
      const res = await axiosInstance.post(
        `${API_URL}/${ONBOARDING_SERVICE_PATH}/change-language/${proceedToken}`,
        {
          language: language,
        },
        {
          withCredentials: false,
        }
      )

      if (res.data.newLanguage) {
        store.TranslationsState.setTranslations(res.data.newLanguage as Language)
        const url = new URL(window.location.href)
        url.searchParams.set('language', String(language))
        window.history.replaceState({}, '', url)
      }
    } catch (error) {
      devLog('error: ', error)
    } finally {
      setSwitchingLanguage(false)
    }
  }

  useEffect(() => {
    if (width && width <= 980) {
      setMobileContainerHeight(`calc(${height}px - 120px)`)
    }
  }, [width, height])

  useEffect(() => {
    store.InterfaceState.setSendBeforeunloadEvent(false)

    getUserData()
  }, [])

  useEffect(() => {
    if (!proceedToken || welcomeData?.isTokenExpired) {
      forceRedirect(`${API_URL}/error-transaction-expired`)
      return
    }
  }, [proceedToken, welcomeData])

  const partiallyResetState = () => {
    setBtnLoading(false)
    setIsChangeLanguagePossible(false)
  }

  useEffect(() => {
    window.addEventListener('pageshow', partiallyResetState)

    return () => {
      window.removeEventListener('pageshow', partiallyResetState)
    }
  }, [])

  return (
    <div className="page-container">
      {loading || switchingLanguage ? (
        <>
          <LoadingSpinner />
          {switchingLanguage && (
            <p className="generic__paragraph-centered">{trans.changingLanguage}...</p>
          )}
        </>
      ) : (
        <>
          <div
            className="layout__content-container-with-scroll"
            style={
              isMobile
                ? ({ '--ccws-mobile-height': mobileContainerHeight } as CSSProperties)
                : undefined
            }
          >
            {isChangeLanguagePossible && (
              <div className="language-select__container">
                <LanguageSelect
                  value={language}
                  options={LANGUAGES_LIST}
                  disabled={false}
                  onChange={(language) => handleChangeLanguage(language)}
                />
              </div>
            )}

            <Header>{trans.header}</Header>

            <div className="layout__margined m-0-0-15" />

            <Illustration type="sign-document" />
            <p className="generic__paragraph-centered">{trans.description1}</p>
            <div className="layout__margined m-0-0-15" />
            <p className="generic__paragraph-centered">{trans.description2}</p>
            <div className="layout__margined m-0-0-15" />

            <p className="generic__paragraph-centered">{trans.makeSureFullNameIsCorrect}</p>
            <div className="enpal__step">
              <div className="enpal__info-text-centered">
                <strong>
                  {welcomeData?.firstName} {welcomeData?.lastName}
                </strong>
              </div>
            </div>
            <p className="generic__paragraph-centered">{trans.noteIdMatch}</p>

            <div className="layout__margined m-0-0-30" />
            <div className="enpal__step">
              <div className="enpal__step-icon">
                <Icon type="id" color="var(--icon-main-color, black)" size="32px" />
              </div>
              <div className="enpal__info-text">{trans.pleaseHaveIdReady}</div>
            </div>

            {isMobile ? (
              <div className="layout__margined m-0-0-80" />
            ) : (
              <div className="layout__margined m-0-0-15" />
            )}
          </div>

          <div className="layout__content-container-bottom-fixed">
            {!isMobile ? <div className="layout__content-container-bottom-left" /> : <></>}
            <Button onClick={handleProceed} className="button--w164" id="page-submit" type="submit">
              {btnLoading && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  btnLoading && 'generic__button-text--loading'
                )}
              >
                {trans.continue}
              </span>
            </Button>
          </div>
        </>
      )}
    </div>
  )
})
